import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, ChevronDown, Check } from "lucide-react";
import { CATEGORIES, FLAVORS } from "../mock";
import { fetchProducts } from "../lib/api";
import ProductCard from "../components/ProductCard";
import Seo from "../components/Seo";

const SORTS = [
  { key: "featured", label: "Featured" },
  { key: "best", label: "Best selling" },
  { key: "az", label: "Alphabetically, A-Z" },
  { key: "za", label: "Alphabetically, Z-A" },
  { key: "plh", label: "Price, low to high" },
  { key: "phl", label: "Price, high to low" },
];

const Collection = () => {
  const { slug = "all" } = useParams();
  const [searchParams] = useSearchParams();
  const q = (searchParams.get("q") || "").toLowerCase();

  const [sort, setSort] = useState("featured");
  const [sortOpen, setSortOpen] = useState(false);
  const [flavorFilter, setFlavorFilter] = useState([]);
  const [maxPrice, setMaxPrice] = useState(15000);
  const [showFilters, setShowFilters] = useState(false);

  const category = CATEGORIES.find((c) => c.slug === slug) || CATEGORIES[0];

  const [allProducts, setAllProducts] = useState([]);
  useEffect(() => {
    fetchProducts(slug).then(setAllProducts).catch(() => setAllProducts([]));
  }, [slug]);

  const products = useMemo(() => {
    let list = [...allProducts];
    if (q) list = list.filter((p) => p.name.toLowerCase().includes(q) || p.flavor.toLowerCase().includes(q) || p.categories.join(" ").includes(q));
    if (flavorFilter.length) list = list.filter((p) => flavorFilter.includes(p.flavor));
    list = list.filter((p) => p.price <= maxPrice);
    const sorted = [...list];
    switch (sort) {
      case "best": sorted.sort((a, b) => b.reviews - a.reviews); break;
      case "az": sorted.sort((a, b) => a.name.localeCompare(b.name)); break;
      case "za": sorted.sort((a, b) => b.name.localeCompare(a.name)); break;
      case "plh": sorted.sort((a, b) => a.price - b.price); break;
      case "phl": sorted.sort((a, b) => b.price - a.price); break;
      default: sorted.sort((a, b) => (b.bestseller ? 1 : 0) - (a.bestseller ? 1 : 0));
    }
    return sorted;
  }, [allProducts, q, sort, flavorFilter, maxPrice]);

  const toggleFlavor = (f) => setFlavorFilter((prev) => (prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]));

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
      <Seo
        path={`/collections/${slug}`}
        title={q ? `Search results for "${q}"` : `${category.name} — Online Delivery in Noida & Greater Noida`}
        description={`Order ${category.name.toLowerCase()} online from Globaltreats with next-day delivery across Noida, Greater Noida & Indirapuram. Freshly baked and beautifully hand-decorated.`}
        noindex={!!q}
      />
      {/* Breadcrumb + title */}
      <div className="mb-6">
        <div className="text-sm text-[#B07A5C] mb-2"><Link to="/" className="hover:text-[#D46A8A]">Home</Link> / <span className="text-[#6B4226]">{category.name}</span></div>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-[#3A2417]">{q ? `Search: “${q}”` : category.name}</h1>
        <p className="text-[#B07A5C] mt-1">{products.length} delicious {products.length === 1 ? "cake" : "cakes"}</p>
      </div>

      {/* Category pills */}
      <div className="flex gap-2 overflow-x-auto pb-3 mb-6">
        {CATEGORIES.map((c) => (
          <Link key={c.slug} to={`/collections/${c.slug}`}
            className={`whitespace-nowrap text-sm px-4 py-2 rounded-full border transition-colors ${c.slug === slug && !q ? "bg-[#D46A8A] text-white border-[#D46A8A]" : "bg-white text-[#6B4226] border-[#EADBCB] hover:border-[#D46A8A]"}`}>
            {c.name}
          </Link>
        ))}
      </div>

      <div className="flex gap-8">
        {/* Sidebar filters */}
        <aside className={`${showFilters ? "fixed inset-0 z-[75] bg-black/40" : "hidden"} md:static md:block md:z-auto md:bg-transparent w-full md:w-64 shrink-0`} onClick={() => setShowFilters(false)}>
          <div className="bg-[#FFF8F0] md:bg-transparent h-full md:h-auto w-72 md:w-full ml-auto md:ml-0 p-5 md:p-0 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="bg-white rounded-2xl p-5 bakery-shadow md:sticky md:top-40">
              <h3 className="font-display text-lg font-semibold text-[#3A2417] mb-4">Filters</h3>
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-[#6B4226] mb-3">Flavour</h4>
                <div className="space-y-2">
                  {FLAVORS.map((f) => (
                    <button key={f} onClick={() => toggleFlavor(f)} className="flex items-center gap-2.5 text-sm text-[#6B4226] w-full">
                      <span className={`w-5 h-5 rounded-md border grid place-items-center ${flavorFilter.includes(f) ? "bg-[#D46A8A] border-[#D46A8A]" : "border-[#EADBCB]"}`}>
                        {flavorFilter.includes(f) && <Check size={13} className="text-white" />}
                      </span>
                      {f}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-[#6B4226] mb-3">Max Price: ₹{maxPrice.toLocaleString("en-IN")}</h4>
                <input type="range" min="500" max="15000" step="100" value={maxPrice} onChange={(e) => setMaxPrice(Number(e.target.value))} className="w-full accent-[#D46A8A]" />
              </div>
              {(flavorFilter.length > 0 || maxPrice < 15000) && (
                <button onClick={() => { setFlavorFilter([]); setMaxPrice(15000); }} className="mt-5 text-sm text-[#D46A8A] hover:underline">Clear all filters</button>
              )}
            </div>
          </div>
        </aside>

        {/* Grid */}
        <div className="flex-1">
          <div className="flex items-center justify-between mb-5">
            <button onClick={() => setShowFilters(true)} className="md:hidden inline-flex items-center gap-2 text-sm text-[#6B4226] bg-white border border-[#EADBCB] rounded-full px-4 h-10">
              <SlidersHorizontal size={16} /> Filters
            </button>
            <div className="relative ml-auto">
              <button onClick={() => setSortOpen((o) => !o)} className="inline-flex items-center gap-2 text-sm text-[#6B4226] bg-white border border-[#EADBCB] rounded-full px-4 h-10 hover:border-[#D46A8A]">
                Sort: {SORTS.find((s) => s.key === sort)?.label} <ChevronDown size={16} />
              </button>
              {sortOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl bakery-shadow border border-[#EADBCB] py-2 z-30">
                  {SORTS.map((s) => (
                    <button key={s.key} onClick={() => { setSort(s.key); setSortOpen(false); }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-[#F6E7DA] ${sort === s.key ? "text-[#D46A8A] font-medium" : "text-[#6B4226]"}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {products.length === 0 ? (
            <div className="text-center py-20 text-[#B07A5C]">No cakes found. Try adjusting your filters.</div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 md:gap-7">
              {products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Collection;
